/*
 * Ryan O'Dowd
 * 2024-04-14
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  addNewPassagesOfTheDay,
  fetchAllPassagesOfTheDay,
  fetchBookNames,
  fetchBooks,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Checkbox from '@components/common/Checkbox';
import Dialog from '@components/common/Dialog';
import Passage from '@components/Passage';
import Table from '@components/common/Table';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';

const PassagesOfTheDayTable = () => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_isAddPassageDialogOpen, setIsAddPassageDialogOpen] = useState(false);
  const [_showAll, setShowAll] = useState(false);
  const [_passagesText, setPassageText] = useState('');
  const [_hasShownWarning, setHasShownWarning] = useState(false);

  useEffect(() => {
    dispatch(fetchBooks());
    dispatch(fetchBookNames());
    dispatch(fetchAllPassagesOfTheDay());
  }, [dispatch]);

  const passagesOfTheDay = hooks.useTypedSelector((state) => state.allPassagesOfTheDay);
  const oneDay = 1000 * 60 * 60 * 24;
  const tableRows = Object.values(passagesOfTheDay).filter((p: any) => _showAll ? true : new Date(p.date_active).getTime() >= (Date.now() - oneDay)).map((p: any) => {
    const passage = new CPassage(-1, 3, p.book_id, p.chapter_number, p.start_verse_number, p.end_verse_number);

    const passageDate = new Date(p.date_active);
    const month = passageDate.toLocaleString('en-US', {month: 'long', timeZone: 'UTC'});
    const day = passageDate.getUTCDate();
    const year = passageDate.getUTCFullYear();

    const utcFormattedDate = `${month} ${day}, ${year}`; // @TODO: should this use dateFnsFormat instead?

    return [
      {
        id: `${p.id}_date_active`,
        content: utcFormattedDate,
        sortValue: new Date(p.date_active),
        styles: {
          flexShrink: 1,
        },
      },
      {
        id: `${p.id}_passage`,
        content: (
          <Passage// @TODO: combine with assignments/versememory and assignments/reading (some props are different)
            passage={passage}
            hideActions={true}
            hideSelectionMenu={true}
            hideWrinkleBadges={true}
            hideProgressStyling={true}
            showHeadings={false}
            isExpanded={false}
            isPressDisabled={true}
          />
        ),
        styles: {
        },
        sortValue: 0, // @TODO: really doesn't need to be sortable
      },
    ];
  });

  if (!_hasShownWarning && tableRows.length <= 14) {
    window.alert(`Please add more Passages Of The Day.  Only ${tableRows.length} remaining.`);
    setHasShownWarning(true);
  }

  return (
    <View style={styles.container}>
      <View style={styles.sectionWrapper}>
        <Checkbox
          isChecked={_showAll}
          color={styles.colors.accent}
          onPress={() => setShowAll((curr) => !curr)}
          label='Show all'
        />
      </View>
      <Table
        tableTitle='Passages'
        open={true}
        headerRowData={[
          {width: 90, id: 'date', content: 'Date'},
          {width: 240, id: 'passage', content: 'Passage', rightAlign: true},
        ]}
        isSortable={true}
        orderByIndex={0}
        orderDirection='ascending'
        rowData={tableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        filtersJsx={
          <Button
            theme={styles.colors.accent}
            onPress={() => setIsAddPassageDialogOpen(true)}
          >
            Add passages
          </Button>
        }
      />

      <Dialog
        visible={_isAddPassageDialogOpen}
        onDismiss={() => setIsAddPassageDialogOpen(false)}
      >
        <Dialog.Title>Add passages</Dialog.Title>{/* @TODO: i18n */}
        <Dialog.Content>
          <TextInput
            mode='outlined'
            value={_passagesText}
            onChangeText={(text) => setPassageText(text)}
            label='Passages'
            // @TODO: paper equivalent? helperText='Comma-separated: e.g. `John 3:16,Romans 8:9-10`'
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='text'
            onPress={() => setIsAddPassageDialogOpen(false)}
          >
            {i18n('cancel')}
          </Button>
          <Button
            theme={styles.colors.accent}
            onPress={() => {
              // @TODO: form validation
              dispatch(addNewPassagesOfTheDay(_passagesText));
              setIsAddPassageDialogOpen(false);
              setPassageText('');
            }}
          >
            Save passages
          </Button>
        </Dialog.Actions>
      </Dialog>
    </View>
  );
};

export default PassagesOfTheDayTable;
